import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import Layout from '../components/Layout';

interface Memory {
  id: string;
  _id?: string;
  content: string;
  category: string;
  privacy_level: string;
  confidence: number;
  created_at: string;
  updated_at: string;
  user_id: string;
  org_id?: string;
  agent_id?: string;
  source_thread_id?: string;
  vector_id?: string;
}

interface MemoryResponse {
  items: Memory[];
  total: number;
}

export default function Memory() {
  const { t } = useTranslation();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const fetchMemories = async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      
      const params: any = {
        limit: itemsPerPage,
        offset,
        sort_by: 'created_at',
        sort_direction: 'desc',
      };
      
      if (selectedCategory) {
        params.category = [selectedCategory];
      }
      
      let endpoint;
      if (searchQuery.trim()) {
        endpoint = '/api/memory/client/search';
        params.query = searchQuery;
      } else {
        endpoint = '/api/memory/client';
      }
      
      const response = await api.get<MemoryResponse>(endpoint, { params });
      
      // Обрабатываем все записи, которые получены с сервера
      const processedItems = response.data.items.map((memory, index) => {
        // Если _id есть и не пустой, используем его
        if (memory._id && memory._id !== "" && !memory.id) {
          return { 
            ...memory, 
            id: memory._id
          };
        }
        // Если id и _id отсутствуют или пустые, генерируем временный id
        if ((!memory.id || memory.id === "") && (!memory._id || memory._id === "")) {
          return {
            ...memory,
            id: `temp-${index}-${Date.now()}-${memory.content.substring(0, 10).replace(/\s/g, '-')}`
          };
        }
        return memory;
      });
      
      // Отображаем все записи - фильтрация уже произошла на сервере
      console.log("Loaded memories:", processedItems.length);
      setMemories(processedItems);
      setTotalItems(response.data.total);
    } catch (err) {
      setError(t('memory.error.load_failed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemories();
  }, [currentPage, itemsPerPage, selectedCategory]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMemories();
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handleDeleteMemory = async (id: string) => {
    // Для временных ID просто обновляем локально
    if (id && id.startsWith('temp-')) {
      setMemories(prevMemories => prevMemories.filter(m => m.id !== id));
      alert(t('memory.success.deleted'));
      return;
    }
    
    // Проверка на валидный ID
    if (!id || id === 'undefined' || id === '') {
      console.error('Invalid memory ID for deletion');
      setError(t('memory.error.delete_failed'));
      return;
    }
    
    if (window.confirm(t('memory.delete_confirmation.description'))) {
      try {
        console.log("Deleting memory with ID:", id);
        await api.delete(`/api/memory/${id}`);
        fetchMemories();
        alert(t('memory.success.deleted'));
      } catch (err) {
        setError(t('memory.error.delete_failed'));
        console.error(err);
        alert(t('memory.error.delete_failed'));
      }
    }
  };
  
  const handleUpdatePrivacyLevel = async (memory: Memory, privacyLevel: string) => {
    try {
      // Для временных ID не выполняем обновление на сервере
      if (memory.id && memory.id.startsWith('temp-')) {
        alert(t('memory.error.update_failed') + ": Cannot update temporary memory record");
        return;
      }
      
      // Используем memory._id если id отсутствует
      const memoryId = memory.id || memory._id;
      
      // Дополнительная проверка ID
      if (!memoryId || memoryId === 'undefined' || memoryId === '') {
        console.error('Invalid memory ID for privacy update');
        setError(t('memory.error.update_failed'));
        return;
      }
      
      console.log("Updating privacy level for memory:", memoryId, "to", privacyLevel);
      await api.put(`/api/memory/${memoryId}`, { privacy_level: privacyLevel });
      fetchMemories();
      alert(t('memory.success.privacy_updated'));
    } catch (err) {
      setError(t('memory.error.update_failed'));
      console.error(err);
      alert(t('memory.error.update_failed'));
    }
  };
  
  const getCategoryDisplayName = (category: string) => {
    const categoryKey = category.toLowerCase();
    return t(`memory.categories.${categoryKey}`, category);
  };
  
  const renderConfidenceLevel = (confidence: number) => {
    if (confidence >= 0.8) return <span className="text-green-500">{(confidence * 100).toFixed(0)}%</span>;
    if (confidence >= 0.6) return <span className="text-yellow-500">{(confidence * 100).toFixed(0)}%</span>;
    return <span className="text-red-500">{(confidence * 100).toFixed(0)}%</span>;
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <Layout title={t('memory.title')}>
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-[#00C2CB] mb-6">{t('memory.title')}</h1>
        <p className="text-white mb-8">{t('memory.description')}</p>
        
        {/* Search form */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <input
            type="text"
            placeholder={t('memory.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#1A2235] text-white border border-[#253045] rounded p-2 flex-1"
          />
          <select 
            value={selectedCategory || ""}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="bg-[#1A2235] text-white border border-[#253045] rounded p-2 w-48"
          >
            <option value="">{t('memory.categories.all')}</option>
            <option value="PERSONAL_INFO">{t('memory.categories.personal')}</option>
            <option value="WORK_PREFERENCES">{t('memory.categories.work')}</option>
            <option value="TECHNICAL_KNOWLEDGE">{t('memory.categories.technical')}</option>
            <option value="INTERACTION_HISTORY">{t('memory.categories.interaction')}</option>
            <option value="ORGANIZATION_CONTEXT">{t('memory.categories.organization')}</option>
          </select>
          <button
            type="submit"
            className="bg-[#00C2CB] text-white py-2 px-4 rounded hover:bg-[#00A2AB]"
          >
            {t('memory.actions.search')}
          </button>
        </form>
        
        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-white animate-pulse">{t('common.loading')}</div>
          </div>
        ) : error ? (
          <div className="bg-red-500 text-white p-4 rounded mb-4">{error}</div>
        ) : memories.length === 0 ? (
          <div className="bg-[#1A2235] text-white p-6 rounded text-center">
            {t('memory.no_records')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full bg-[#1A2235] rounded-lg overflow-hidden">
              <thead className="bg-[#253045] text-[#00C2CB]">
                <tr>
                  <th className="p-3 text-left">{t('memory.table.content')}</th>
                  <th className="p-3 text-left">{t('memory.table.category')}</th>
                  <th className="p-3 text-left">{t('memory.table.access')}</th>
                  <th className="p-3 text-left">{t('memory.table.confidence')}</th>
                  <th className="p-3 text-left">{t('memory.table.created_at')}</th>
                  <th className="p-3 text-right">{t('memory.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="text-white">
                {memories.map((memory) => (
                  <tr key={memory.id || memory._id} className="border-t border-[#253045]">
                    <td className="p-3 max-w-xs truncate">{memory.content}</td>
                    <td className="p-3">{getCategoryDisplayName(memory.category)}</td>
                    <td className="p-3">
                      <select
                        value={memory.privacy_level}
                        onChange={(e) => handleUpdatePrivacyLevel(memory, e.target.value)}
                        className="bg-[#1A2235] text-white border border-[#253045] rounded p-1"
                      >
                        <option value="agent">{t('memory.privacy_levels.agent')}</option>
                        <option value="organization">{t('memory.privacy_levels.organization')}</option>
                        {/* Если запись приватная, показываем этот вариант */}
                        {(memory.privacy_level === 'private' || memory.privacy_level === 'PRIVATE') && (
                          <option value="private">Private</option>
                        )}
                      </select>
                    </td>
                    <td className="p-3">{renderConfidenceLevel(memory.confidence)}</td>
                    <td className="p-3">{new Date(memory.created_at).toLocaleString()}</td>
                    <td className="p-3 text-right">
                      <button 
                        onClick={() => handleDeleteMemory(memory.id || memory._id || '')}
                        className="bg-red-600 text-white py-1 px-3 rounded text-sm hover:bg-red-700"
                      >
                        {t('memory.actions.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${
                  currentPage === 1
                    ? 'bg-[#253045] text-gray-500'
                    : 'bg-[#1A2235] text-white hover:bg-[#253045]'
                }`}
              >
                &laquo;
              </button>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${
                  currentPage === 1
                    ? 'bg-[#253045] text-gray-500'
                    : 'bg-[#1A2235] text-white hover:bg-[#253045]'
                }`}
              >
                &lsaquo;
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                const pageNum = currentPage > 3 
                  ? currentPage - 3 + index + 1 
                  : index + 1;
                
                if (pageNum <= totalPages) {
                  return (
                <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 rounded ${
                        currentPage === pageNum
                      ? 'bg-[#00C2CB] text-white'
                      : 'bg-[#1A2235] text-white hover:bg-[#253045]'
                  }`}
                >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}
              
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${
                  currentPage === totalPages
                    ? 'bg-[#253045] text-gray-500'
                    : 'bg-[#1A2235] text-white hover:bg-[#253045]'
                }`}
              >
                &rsaquo;
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${
                  currentPage === totalPages
                    ? 'bg-[#253045] text-gray-500'
                    : 'bg-[#1A2235] text-white hover:bg-[#253045]'
                }`}
              >
                &raquo;
                </button>
            </div>
            
            <select 
              value={itemsPerPage} 
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="bg-[#1A2235] text-white border border-[#253045] rounded p-2"
            >
              <option value={5}>5 {t('memory.per_page')}</option>
              <option value={10}>10 {t('memory.per_page')}</option>
              <option value={20}>20 {t('memory.per_page')}</option>
              <option value={50}>50 {t('memory.per_page')}</option>
            </select>
          </div>
        )}
        
        {/* Total count */}
        <div className="text-white mt-6">
          {t('memory.total_records')}: {totalItems}
        </div>
      </div>
    </Layout>
  );
} 